import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { debounce } from 'lodash';
import './App.css';
import Controls from './Components/Controls/Controls';
import Intro from './Components/Intro/Intro';
import Onboarding from './Components/Onboarding/Onboarding';
import ControlsBar from './Components/Controls/ControlsBar';

// What the writer set out before they started: the subject, the voice, the
// material, any images. Read here so a request can carry it.
const readBrief = () => {
  try {
    const raw = localStorage.getItem('itype-brief');
    if (!raw) return null;
    const brief = JSON.parse(raw);
    const something = brief && (brief.topic || brief.voice || brief.notes || (brief.images || []).length);
    return something ? brief : null;
  } catch {
    return null;
  }
};

const ITYPE = () => {
  const [text, setText] = useState(() => localStorage.getItem('itype-text') || '');
  const [currentSuggestion, setCurrentSuggestion] = useState('');
  const [fontSize, setFontSize] = useState(() => parseInt(localStorage.getItem('itype-fontSize')) || 80);
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [showWordCount, setShowWordCount] = useState(true);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  // The name types itself, then the door is open until someone walks through
  // it, either into the brief or straight onto the page.
  const [intro, setIntro] = useState(true);
  const [started, setStarted] = useState(false);
  // On a phone the keys are drawn at the bottom of the screen, and drawn keys
  // over a page nobody is typing on are just a wall in the way: they covered
  // the brief. So they arrive when the writing does.
  const [writing, setWriting] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 420);
  const [isReady, setIsReady] = useState(false);
  const [briefOpen, setBriefOpen] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  // Whether a suggestion is on its way, so a slow answer reads as thinking
  // rather than as a dead page.
  const [pending, setPending] = useState(false);
  // Only for the badge: the request path reads storage directly, so this is
  // display state and nothing depends on it being in sync to the millisecond.
  const [briefSummary, setBriefSummary] = useState(() => readBrief()?.topic || '');

  // Whether the full screen questions are up, for the key handler, which is
  // bound once and must not close over stale state.
  const onboardingOpen = useRef(true);
  useEffect(() => { onboardingOpen.current = !started || briefOpen; }, [started, briefOpen]);

  const contentRef = useRef(null);
  const controlBarRef = useRef(null);
  const containerRef = useRef(null);

  // What the model offered this time, and which one is on screen.
  //
  // One suggestion was the whole problem: the only way to get a different one
  // was to ask again, and asking again with the same words gave the same
  // words back. A request brings several now, Up walks through them, and what
  // has already been shown goes back up as things not to repeat.
  const suggestionRef = useRef('');
  const candidatesRef = useRef([]);
  const candidateIndexRef = useRef(0);
  const shownRef = useRef([]);
  const requestRef = useRef(0);

  const showCandidate = useCallback((baseText, index) => {
    const list = candidatesRef.current;
    if (!list.length) return;
    const next = ((index % list.length) + list.length) % list.length;
    candidateIndexRef.current = next;
    const suggestion = list[next];
    if (suggestion && !shownRef.current.includes(suggestion)) {
      shownRef.current = [...shownRef.current, suggestion].slice(-6);
    }
    const needsSpace = Boolean(baseText) && !baseText.endsWith(' ') && !/^[,.;:!?'’)]/.test(suggestion);
    // The rest of the app reads this as the whole line: what is typed plus
    // what is offered. Keeping that shape leaves the accept keys untouched.
    suggestionRef.current = baseText + (needsSpace ? ' ' : '') + suggestion;
    setCurrentSuggestion(suggestionRef.current);
  }, []);

  const fetchSuggestions = useCallback(async (baseText) => {
    if (!(baseText || '').trim()) return;
    const ticket = ++requestRef.current;
    setPending(true);
    try {
      const brief = readBrief();
      const { data } = await axios.post('/.netlify/functions/prediction', {
        text: baseText,
        avoid: shownRef.current,
        brief: brief || undefined,
        n: 3,
      // The function gives up at nine seconds, so the page holds on for ten
      // and no longer. Whether a late answer is shown is decided by the
      // ticket check below, not by this timeout: if anything has been typed
      // since, it is dropped.
      }, { timeout: 10000 });
      // A slow answer to an old keystroke must not overwrite a newer one.
      if (ticket !== requestRef.current) return;
      const list = Array.isArray(data.suggestions) ? data.suggestions : [];
      candidatesRef.current = list
        .map((sugg) => sugg.replace(/[\n↵*]/g, ' ').replace(/\s+/g, ' ').trim())
        .filter(Boolean);
      candidateIndexRef.current = 0;
      if (candidatesRef.current.length) showCandidate(baseText, 0);
    } catch (error) {
      if (ticket !== requestRef.current) return;
      // A suggestion that did not arrive is not an error the writer needs to
      // see. It just means no ghost text this time.
      const status = error?.response?.status;
      if (status !== 504 && error.code !== 'ECONNABORTED') {
        console.error('Could not reach the model:', error?.response?.data?.error || error.message);
      }
    } finally {
      if (ticket === requestRef.current) setPending(false);
    }
  }, [showCandidate]);

  // Half a second of silence before asking, plus about 650ms of answer, was
  // over a second of nothing after the last keystroke. The answers are short
  // now, so the wait in front of them can be short too.
  const debouncedFetchSuggestions = useMemo(
    () => debounce((value) => fetchSuggestions(value), 280),
    [fetchSuggestions]
  );

  // Ask only when there is nothing left to offer.
  //
  // Taking a word used to throw away the rest of the suggestion and ask for a
  // new one, so every accepted word cost a round trip and the page went blank
  // while it waited. The rest of the sentence is already on screen: if what is
  // offered still starts with what has been typed, that is the answer and no
  // request is needed. Typing a letter the machine had already guessed counts
  // too, which is why taking words now feels instant even when the model is
  // having a slow afternoon.
  const askIfNeeded = useCallback((value) => {
    const offered = suggestionRef.current;
    if (value && offered.length > value.length && offered.startsWith(value)) {
      debouncedFetchSuggestions.cancel();
      setPending(false);
      return;
    }
    if (offered && !offered.startsWith(value)) {
      suggestionRef.current = '';
      setCurrentSuggestion('');
      candidatesRef.current = [];
      candidateIndexRef.current = 0;
    }
    debouncedFetchSuggestions(value);
  }, [debouncedFetchSuggestions]);

  useEffect(() => {
    localStorage.setItem('itype-fontSize', fontSize.toString());
    
    // Update word and character count
    const trimmedText = text.trim();
    const words = trimmedText.split(/\s+/);
    setWordCount(trimmedText ? words.length : 0);
    setCharCount(text.length);

    // Smoother cinematic font size adjustment
    if (text.length > 25) {
      // Logarithmic decrease for smoother scaling
      const decrease = Math.log(text.length - 24) * 8;
      const newSize = Math.max(25, 80 - decrease);
      setFontSize(newSize);
    } else {
      setFontSize(80); // Reset to default size for short text
    }
  }, [text, fontSize]);

  useEffect(() => {
    const handleKeyboardShortcuts = (e) => {
      if (e.altKey && e.key.toLowerCase() === 'w') {
        e.preventDefault();
        setShowWordCount(prev => !prev);
        return;
      }

      // Everything but the words. Alt+F, and Escape brings it all back.
      if (e.altKey && e.key.toLowerCase() === 'f') {
        e.preventDefault();
        setFocusMode((on) => !on);
        return;
      }

      // The questions. Ctrl+B opens them; the screen itself owns Escape from
      // there, because closing it from out here unmounted it mid animation and
      // its own goodbye, the one that asks for a fresh suggestion, never ran.
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'b') {
        e.preventDefault();
        if (!onboardingOpen.current) setBriefOpen(true);
        return;
      }
      if (e.key === 'Escape') {
        if (!onboardingOpen.current) setFocusMode(false);
        return;
      }

      if (e.ctrlKey || e.metaKey) {
        switch(e.key.toLowerCase()) {
          case '+':
          case '=':
            e.preventDefault();
            setFontSize(prev => Math.min(prev + 2, 120));
            break;
          case '-':
            e.preventDefault();
            setFontSize(prev => Math.max(prev - 2, 40));
            break;
          default:
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyboardShortcuts);
    return () => document.removeEventListener('keydown', handleKeyboardShortcuts);
  }, []);

  const handleEditableInput = useCallback((e) => {
    if (!contentRef.current || !isReady) return;
    
    const newText = e.target.innerText;
    setText(newText);
    askIfNeeded(newText);
  }, [isReady, askIfNeeded]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 580) {
        setIsMobile(true);
      } else {
        setIsMobile(false);
      }
    };

    window.addEventListener('resize', handleResize);

    // Initial check
    handleResize();

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if ('virtualKeyboard' in navigator) {
      navigator.virtualKeyboard.overlaysContent = true;
      navigator.virtualKeyboard.hide();
    }
  }, []);

  useEffect(() => {
    if (!isReady || !containerRef.current) return;

    const updateOverflow = () => {
      const container = containerRef.current;
      if (container && fontSize === 25) {  
        if (container.scrollHeight > container.clientHeight) {
          container.style.overflowY = 'auto';
        } else {
          container.style.overflowY = 'unset';
        }
      } else if (container) {
        container.style.overflowY = 'unset';  
      }
    };

    const timer = setTimeout(updateOverflow, 100);
    return () => clearTimeout(timer);
  }, [text, isReady, fontSize]);

  useEffect(() => {
    const updateCursorPosition = () => {
      if (!isReady) return;
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        setCursorPosition({
          x: rect.right + window.scrollX + 10,
          y: rect.top + window.scrollY - 20
        });
      }
    };

    if (isReady) {
      document.addEventListener('selectionchange', updateCursorPosition);
      return () => document.removeEventListener('selectionchange', updateCursorPosition);
    }
  }, [isReady]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const moveCursorToEnd = useCallback(() => {
    if (!contentRef.current || !isReady) return;
    
    const range = document.createRange();
    const selection = window.getSelection();
    range.selectNodeContents(contentRef.current);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  }, [isReady]);

  const moveCursorOneStepLeft = useCallback(() => {
    if (!contentRef.current || !isReady) return;

    const selection = window.getSelection();
    if (selection.rangeCount) {
      const range = selection.getRangeAt(0);
      range.setStart(range.startContainer, Math.max(0, range.startOffset - 1));
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }, [isReady]);

  const moveCursorOneStepRight = useCallback(() => {
    if (!contentRef.current || !isReady) return;

    const selection = window.getSelection();
    if (selection.rangeCount) {
      try {
        selection.modify("move", "forward", "character");
      } catch (error) {
        // Handle any unexpected errors gracefully
        console.error('Error moving cursor right:', error);
        const range = selection.getRangeAt(0);
        selection.removeAllRanges();
        range.setStart(range.startContainer, range.startOffset + 1);
        selection.addRange(range);
      }
    }
  }, [isReady]);

  const generateTitleAndSave = useCallback(async () => {
    try {
      // Save to localStorage
      localStorage.setItem('itype-text', text);

      const titleResponse = await axios.post('/.netlify/functions/generate-title', { text });
      const title = titleResponse.data.title;

      const reformatResponse = await axios.post('/.netlify/functions/reformat-text', { title, text });
      const formattedText = reformatResponse.data.formattedText;

      // Save the text using the browser's save dialog
      const blob = new Blob([formattedText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title}.txt`
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating title and saving the text:', error);
    }
  }, [text]);

  useEffect(() => {
    if (text.length > 0) {
      askIfNeeded(text);
    } else {
      suggestionRef.current = '';
      setCurrentSuggestion('');
      if (contentRef.current && isReady) {
        contentRef.current.focus();
      }
    }
  }, [text, askIfNeeded, isReady]);

  useEffect(() => {
    if (!isReady || !containerRef.current) return;

    const updateOverflow = () => {
      const container = containerRef.current;
      if (container && fontSize === 25) {  
        if (container.scrollHeight > container.clientHeight) {
          container.style.overflowY = 'auto';
        } else {
          container.style.overflowY = 'unset';
        }
      } else if (container) {
        container.style.overflowY = 'unset';  
      }
    };

    const timer = setTimeout(updateOverflow, 100);
    return () => clearTimeout(timer);
  }, [text, isReady, fontSize]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && (event.key === 'c' || event.key === 'v' || event.key === 'x' || event.key === 'a')) {
        event.preventDefault();
      }

      // Handle Ctrl+Shift+ArrowRight separately
      if (event.ctrlKey && event.shiftKey && event.key === 'ArrowRight') {
        event.preventDefault();
        event.stopPropagation();
        setText((prevText) => {
          const newText = prevText + currentSuggestion.slice(prevText.length);
          if (contentRef.current) {
            contentRef.current.innerText = newText;
          }
          setTimeout(() => {
            if (contentRef.current && isReady) {
              moveCursorToEnd();
            }
          }, 0);
          suggestionRef.current = '';
          setCurrentSuggestion('');
          return newText;
        });
        return;
      }

      // Ctrl and right: the next word.
      if (event.ctrlKey && !event.shiftKey && event.key === 'ArrowRight') {
        event.preventDefault();
        event.stopPropagation();
        setText((prevText) => {
          const rest = currentSuggestion.slice(prevText.length);
          if (!rest) return prevText;
          // Up to and including the space, so the caret lands ready for the
          // next word rather than glued to the last one.
          const cut = rest.indexOf(' ', 1);
          const nextText = prevText + (cut === -1 ? rest : rest.slice(0, cut + 1));
          if (contentRef.current) contentRef.current.innerText = nextText;
          setTimeout(() => {
            if (contentRef.current && isReady) moveCursorToEnd();
          }, 0);
          return nextText;
        });
        return;
      }

      // Handle regular ArrowRight
      if (event.key === 'ArrowRight' && !event.ctrlKey && !event.shiftKey) {
        event.preventDefault();
        setText((prevText) => {
          const nextChar = currentSuggestion.charAt(text.length);
          const newText = prevText + nextChar;
          if (contentRef.current) {
            contentRef.current.innerText = newText;
          }
          setTimeout(() => {
            if (contentRef.current && isReady) {
              moveCursorToEnd();
            }
          }, 0);
          return newText;
        });
        return;
      }

      // Prevent multiple spaces
      if (event.key === ' ' && text.endsWith(' ')) {
        event.preventDefault();
      }
      // Prevent multiple newlines
      if (event.key === 'Enter' && text.endsWith('\n')) {
        event.preventDefault();
      }

      // Temporarily prevent Enter key
      if (event.key === 'Enter') {
        event.preventDefault();
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();

        setText((prevText) => {
          const newText = prevText.slice(0, -1); // Calculate the new text here
          if (contentRef.current) {
            contentRef.current.innerText = newText;
          }

          // Use `newText` to set the cursor position
          setTimeout(() => {
            if (contentRef.current && isReady) {
              const selection = window.getSelection();
              const range = document.createRange();
              range.setStart(contentRef.current.childNodes[0], newText.length); // Set the cursor to the correct position
              range.collapse(true);
              selection.removeAllRanges();
              selection.addRange(range);
            }
          }, 0);

          return newText; // Return the updated text for the state update
        });
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        // Another one. Walk what already came back before asking again: that
        // is instant, and it is what "give me a different one" should feel
        // like. Only once they have all been seen does it go back to the
        // model, carrying the seen ones as things not to repeat.
        const list = candidatesRef.current;
        const next = candidateIndexRef.current + 1;
        if (list.length && next < list.length) showCandidate(text, next);
        else fetchSuggestions(text);
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        // Clear suggestions
        candidatesRef.current = [];
        candidateIndexRef.current = 0;
        suggestionRef.current = '';
        setCurrentSuggestion('');
      }

      if (event.ctrlKey && event.key === 's') {
        event.preventDefault();
        // Generate title and save the text
        generateTitleAndSave();
      }

      if (event.ctrlKey && event.shiftKey && event.key === 'S') {
        event.preventDefault();
        // Share the text with web share API
        if (navigator.share) {
          navigator.share({
            title: 'Written with ITYPE',
            text: text,
          });
        }
      }

      // Move cursor with page up and page down keys
      if (event.key === 'PageUp' || event.key === 'PageDown') {
        if (event.key === 'PageUp') {
          moveCursorOneStepLeft();
          event.preventDefault();
        } else {
          moveCursorOneStepRight();
          event.preventDefault();
        }
      }
    };

    const handleDragStart = (event) => {
      event.preventDefault();
    };

    const currentContentRef = contentRef.current;
    window.addEventListener('keydown', handleKeyDown);
    if (currentContentRef && isReady) {
      currentContentRef.addEventListener('dragstart', handleDragStart);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (currentContentRef && isReady) {
        currentContentRef.removeEventListener('dragstart', handleDragStart);
      }
    };
  }, [text, currentSuggestion, debouncedFetchSuggestions, fetchSuggestions, showCandidate, moveCursorToEnd, moveCursorOneStepLeft, moveCursorOneStepRight, isReady, generateTitleAndSave]);

  const handleInput = useCallback((inputText) => {
    if (!contentRef.current || !isReady) return;
    
    setText(inputText);
    if (contentRef.current) {
      contentRef.current.innerText = inputText;
    }
    askIfNeeded(inputText);
  }, [isReady, askIfNeeded]);

  const handleAcceptChar = () => {
    if (!contentRef.current || !isReady) return;

    const nextChar = currentSuggestion.charAt(text.length);
    const newText = text + nextChar;
    setText(newText);
    handleInput(newText);
  };
  
  const handleAcceptWord = () => {
    if (!contentRef.current || !isReady) return;

    const nextWord = currentSuggestion.slice(text.length).split(' ')[0] + ' ';
    const newText = text + nextWord;
    setText(newText);
    handleInput(newText);
  };
  
  const handleAcceptSuggestion = () => {
    if (!contentRef.current || !isReady) return;

    const newText = text + currentSuggestion.slice(text.length);
    setText(newText);
    suggestionRef.current = '';
    setCurrentSuggestion('');
    handleInput(newText);
  };
  
  const getSuggestedText = () => {
    return currentSuggestion.slice(text.length);
  };

  return (
    <div className={`app-container${focusMode ? ' focus' : ''}${started ? ' open' : ''}`}>
      {/* The way in is mounted under the intro rather than after it, so the
          intro fades onto the onboarding and never onto a half second of the
          writing page nobody asked to see yet. */}
      {!started && (
        <Onboarding
          startAt={0}
          hasText={Boolean(text.trim())}
          onDone={() => {
            setStarted(true);
            setBriefSummary(readBrief()?.topic || '');
            candidatesRef.current = [];
            candidateIndexRef.current = 0;
            shownRef.current = [];
            suggestionRef.current = '';
            setCurrentSuggestion('');
            setTimeout(() => contentRef.current?.focus(), 0);
            if (text.trim()) fetchSuggestions(text);
          }}
        />
      )}
      {intro && <Intro onDone={() => setIntro(false)} />}

      {/* Later, ctrl + b puts the same screen back, at the first question. */}
      {started && briefOpen && (
        <Onboarding
          startAt={1}
          hasText={Boolean(text.trim())}
          onDone={() => {
            setBriefOpen(false);
            setBriefSummary(readBrief()?.topic || '');
            // What it knows changed, so what it already offered is stale.
            candidatesRef.current = [];
            candidateIndexRef.current = 0;
            shownRef.current = [];
            suggestionRef.current = '';
            setCurrentSuggestion('');
            setTimeout(() => contentRef.current?.focus(), 0);
            if (text.trim()) fetchSuggestions(text);
          }}
        />
      )}
      {(
        <>
          {/* The count rides beside the caret, so it waits for one: text
              restored from the last session has none until you click in, and
              a count parked at the top left corner of the page is a white box
              nobody asked for. */}
          {isReady && showWordCount && !isMobile && cursorPosition.x > 0 && (
            <div 
              className={`word-count ${text.length > 0 ? 'visible' : ''}`}
              style={{ 
                position: 'absolute',
                left: `${cursorPosition.x}px`,
                top: `${cursorPosition.y}px`,
                fontSize: '14px',
                pointerEvents: 'none'
              }}
            >
              {wordCount} words · {charCount} chars
            </div>
          )}
          <h1 className='logo'>I<span style={{ fontWeight: "100" }}>|</span><span style={{ opacity: "0.3" }}>TYPE</span></h1>

          {/* What it has been told, in one line. It appears only once there is
              something to say: asking for a brief is the start screen's job,
              not a label sitting in the corner of the page. */}
          {isReady && !isMobile && briefSummary && (
            <button
              type="button"
              className="brief-badge"
              onClick={() => setBriefOpen(true)}
              title="Ctrl + B"
            >
              {`On: ${briefSummary}`}
            </button>
          )}
          {/* The hint says tap the page, so the whole page is the target, not
              just the line the words are on. */}
          <div
            className="typewriter-restriction"
            onClick={() => { if (isMobile && !writing) setWriting(true); }}
          >
            <div className="typewriter-container" style={{ fontSize: `${fontSize}px` }} ref={containerRef}>
              <div className="typewriter-wrapper">
                {/* On a phone there is no arrow key to press, so the offer
                    itself is the button: tap the line and it is yours. The
                    input below it takes no taps there anyway, because the
                    keyboard is the one drawn at the bottom of the screen. */}
                <div
                  className="typewriter-input-wrapper"
                  onClick={() => {
                    // Once the keys are up, a tap on the line takes what is
                    // offered on it.
                    if (isMobile && writing && getSuggestedText()) handleAcceptSuggestion();
                  }}
                >
                  <div className="typewriter-suggestion">
                    {text + getSuggestedText()}
                  </div>
                  {pending && !getSuggestedText() && (
                    <div className="thinking" aria-hidden="true">
                      <span />
                      <span />
                      <span />
                    </div>
                  )}
                  <div
                    virtualKeyboardPolicy="manual"
                    ref={contentRef}
                    contentEditable
                    onInput={handleEditableInput}
                    onFocus={(e) => {
                      if (isMobile) {
                        e.target.blur();
                      } else if (isReady) {
                        e.target.focus();
                      }
                    }}
                    className="typewriter-input"
                    style={{ fontSize: `${fontSize}px` }}
                  />
                </div>
              </div>
            </div>
          </div>
          <Controls 
            wordCount={wordCount}
            charCount={charCount}
            showWordCount={showWordCount}
            text={text}
          />
          {(!isMobile || (writing && !briefOpen)) && (
          <ControlsBar
            ref={controlBarRef}
            fontSize={fontSize}
            setFontSize={setFontSize}
            onChange={handleInput}
            currentText={text} 
            onSave={generateTitleAndSave}
            onShare={() => {
              if (navigator.share) {
                navigator.share({
                  title: 'Written with ITYPE',
                  text: text,
                });
              }
            }}
            onBrief={() => setBriefOpen(true)}
            briefSet={Boolean(briefSummary)}
            onAcceptChar={handleAcceptChar}
            onAcceptWord={handleAcceptWord}
            onAcceptSuggestion={handleAcceptSuggestion}
            onDelete={() => {
              setText((prevText) => {
                if (!contentRef.current || !isReady) return prevText;
                const newText = prevText.slice(0, -1);
                contentRef.current.innerText = newText;
                moveCursorToEnd();
                return newText;
              });
            }}
            onRefetch={() => {
              const list = candidatesRef.current;
              const next = candidateIndexRef.current + 1;
              if (list.length && next < list.length) showCandidate(text, next);
              else fetchSuggestions(text);
            }}
            onDiscardSuggestion={() => {
              candidatesRef.current = [];
              candidateIndexRef.current = 0;
              setCurrentSuggestion('');
            }}
          />
          )}

          {/* Nothing happens on a phone until the keys are asked for, so say
              so once, on an empty page. */}
          {isMobile && started && !writing && !text && !briefOpen && (
            <div className="tap-hint">tap the page to start typing</div>
          )}

          {focusMode && <div className="focus-hint">alt + f to come back</div>}
        </>
      )}
    </div>
  );
};

export default ITYPE;
